import * as stylex from '@stylexjs/stylex';

/**
 * Every theme must provide a value for every key listed here.
 * Use `{ ... } satisfies ThemeColorValues` inside createTheme() to enforce it.
 * When you add a token to defineVars below, add the key here too — TypeScript
 * will then error in every theme file until it supplies the new value.
 */
export type ThemeColorValues = {
  readonly primary: string;
  readonly primaryHover: string;
  readonly primaryMuted: string;
  readonly primaryGlow: string;
  readonly background: string;
  readonly surface: string;
  readonly surfaceRaised: string;
  readonly surfaceActive: string;
  readonly surfaceOverlay: string;
  readonly text: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly textInverse: string;
  readonly textOnPrimary: string;
  readonly border: string;
  readonly borderStrong: string;
  readonly divider: string;
  readonly danger: string;
  readonly dangerHover: string;
  readonly dangerMuted: string;
  readonly success: string;
  readonly successMuted: string;
  readonly warning: string;
  readonly warningMuted: string;
  readonly info: string;
  readonly infoMuted: string;
  readonly neutral1: string;
  readonly neutral2: string;
  readonly neutral3: string;
  readonly black: string;
  readonly white: string;
  readonly focus: string;
  readonly hoverOverlay: string;
  readonly activeOverlay: string;

  /** Grow-light magenta — RESTRICTED atmosphere hue (D-072): brand moments
   *  only (login glow, photoperiod headers, empty states); never semantic,
   *  never interactive. */
  readonly atmosphere: string;
  readonly sidebarBg: string;
  /** The sidebar's own ground. Separate from `sidebarBg`, which 9 other
   *  components read as "elevated chrome" (Modal, CommandPalette, …). */
  readonly sidebarSurface: string;
  readonly sidebarText: string;
  readonly sidebarActive: string;
  readonly sidebarActiveBg: string;
  readonly sidebarHoverBg: string;
  readonly sidebarBorder: string;
};

// CSS variable contract. Values here are the light-mode fallbacks used if no
// ThemeProvider wraps a component. Actual theme values live in themes/.
export const colors = {
  primary: "var(--x14q0v20)",
  primaryHover: "var(--xnlda0v)",
  primaryMuted: "var(--xha0etv)",
  primaryGlow: "var(--x4ksqwa)",
  background: "var(--xa7py9f)",
  surface: "var(--x13kkpzn)",
  surfaceRaised: "var(--x14uvqlj)",
  surfaceActive: "var(--x1mvhtuw)",
  surfaceOverlay: "var(--x1uuf4vu)",
  text: "var(--x14zbqd1)",
  textSecondary: "var(--x4rr250)",
  textMuted: "var(--x5mhg0)",
  textInverse: "var(--x1d4wn58)",
  textOnPrimary: "var(--x1g1ujpe)",
  border: "var(--x1r5yzon)",
  borderStrong: "var(--x1365qcs)",
  divider: "var(--xmydzcf)",
  danger: "var(--x1lzvyai)",
  dangerHover: "var(--xi2vz9m)",
  dangerMuted: "var(--x1xpfktn)",
  success: "var(--x1lul24j)",
  successMuted: "var(--x1mnjr5n)",
  warning: "var(--xn8am0u)",
  warningMuted: "var(--x1iuaaj5)",
  info: "var(--xd2e3ef)",
  infoMuted: "var(--xr1017i)",
  neutral1: "var(--xqevc5q)",
  neutral2: "var(--xb38dls)",
  neutral3: "var(--x1o34ndm)",
  black: "var(--xgdslq6)",
  white: "var(--x4cy76r)",
  focus: "var(--xn7suut)",
  hoverOverlay: "var(--xhx1hhl)",
  activeOverlay: "var(--x2hs8zk)",
  sidebarBg: "var(--x1dot9tj)",
  sidebarSurface: "var(--x1o1n54c)",
  sidebarText: "var(--x1cts9up)",
  sidebarActive: "var(--x1pgyw1d)",
  sidebarActiveBg: "var(--x1pygrkr)",
  sidebarHoverBg: "var(--x12l405w)",
  sidebarBorder: "var(--xrk93b3)",
  atmosphere: "var(--x24vn0l)",
  __varGroupHash__: "x1sj0b7k"
};